import React, {FC, ReactNode, useState} from "react";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import {Box, Button, Typography} from "@material-ui/core";
import Alert from "view/components/Alert";
import {Color as Severity} from "@material-ui/lab/Alert";
import clsx from "clsx";
import RejectButton from "view/components/RejectButton";
import FlexBox from "view/components/Layout/FlexBox";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import useStyles from "./styles";

type Props = {
  title: string;
  description?: React.ReactNode;
  onClose?: () => void;
  submitTitle?: string;
  subtitle?: string;
  className?: string;
  severity?: Severity;
  submitColor?: "primary" | "secondary";
  submitVariant?: "contained" | "outlined" | "text";
  submitEndIcon?: React.ReactNode;
  isCalledByAnotherModal?: boolean;
  onSubmit: () => Promise<void> | void;
  titleClassname?: string;
  additionalInfo?: JSX.Element;
  cancelTitle?: string;
  submitStartIcon?: ReactNode;
  /** Theme spacing units above the buttons. */
  actionsTopSpacing?: number;
};

const ConfirmModal: FC<Props> = ({
  title,
  description,
  onClose,
  onSubmit,
  className,
  submitTitle = "Submit",
  subtitle,
  severity = "info",
  submitColor = "primary",
  submitVariant,
  submitEndIcon,
  isCalledByAnotherModal,
  titleClassname,
  additionalInfo,
  cancelTitle,
  submitStartIcon,
  actionsTopSpacing = 3,
}) => {
  const classes = useStyles();
  const t = useLocalizedNameTranslate();
  const cancelText = cancelTitle ?? (t ? t("common.action.cancel") : "Cancel");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit();

      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);

      console.error("Confirm Modal error", e);
    }
  };

  const handleClose = () => {
    if (isSubmitting || !onClose) {
      return;
    }
    onClose();
  };
  return (
    <FfairModal
      open
      onClose={handleClose}
      isCalledByAnotherModal={isCalledByAnotherModal}
    >
      <FfairPaper className={clsx(classes.root, className)}>
        <Box className={classes.header}>
          {subtitle && (
            <Typography variant="body2" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
          <Typography
            variant="h2"
            className={clsx(classes.title, titleClassname)}
          >
            {title}
          </Typography>
        </Box>
        <FlexBox column gap={2} fullWidth>
          {description && <Alert severity={severity}>{description}</Alert>}
          {additionalInfo}
        </FlexBox>
        <Box display="flex" justifyContent="flex-end" pt={actionsTopSpacing}>
          {!!onClose && (
            <Box pr={1}>
              <Button
                color="primary"
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {cancelText}
              </Button>
            </Box>
          )}
          <RejectButton
            endIcon={submitEndIcon}
            variant={submitVariant}
            color={submitColor}
            disabled={isSubmitting}
            onClick={handleSubmit}
            startIcon={submitStartIcon}
          >
            {submitTitle}
          </RejectButton>
        </Box>
      </FfairPaper>
    </FfairModal>
  );
};

export default ConfirmModal;
